import React, { useMemo, useState } from 'react';
import { StylesConfig, ValueType } from 'react-select';
import { Artist, getArtists, getSpotifyArtists, usePaginatedFetch } from '@round/api';
import { GenericDropdownOption } from '../../../../App.types';
import RelatedArtistOption, { RelatedArtistOptionType } from './RelatedArtistOption/RelatedArtistOption';
import { Field, FieldProps, Form, Formik, FormikHelpers, FormikProps } from 'formik';
import Button from '../../../../ui/Buttons/Button/Button';
import FormField from '../../../../ui/DataEntry/FormField/FormField';
import Label from '../../../../ui/DataEntry/Label/Label';
import Select from '../../../../ui/DataEntry/Select/Select';
import FileUpload from '../../../../ui/DataEntry/FileUpload/FileUpload';
import FileUploadImagePreview from '../../../../ui/DataEntry/FileUpload/FileUploadImagePreview/FileUploadImagePreview';
import useAbortableEffect from '../../../../Hooks/useAbortableEffect';
import debounce from 'lodash/debounce';
import styles from './ArtistForm.module.css';

const selectStyles: StylesConfig = {
    control: (base) => ({
        ...base,
        borderColor: '#c2cfe0',
    }),
    valueContainer: (base, state) => ({
        ...base,
        fontSize: '0.8125rem',
        padding: state.isMulti ? '0.625rem 1rem 0.4375rem 1rem' : '0.625rem 1rem',
    }),
    multiValue: (base) => ({
        ...base,
        margin: '0 3px 3px 0',
    }),
    multiValueRemove: (base) => ({
        ...base,
        height: '1rem',
    }),
    placeholder: (base) => ({
        ...base,
        color: '#90a0b7',
    }),
    dropdownIndicator: (base) => ({
        ...base,
        color: '#90a0b7',
    }),
    menuPortal: (provided) => ({
        ...provided,
        zIndex: 1000,
    }),
};

type ArtistFormValues = Pick<Artist, 'name'> & {
    new_image: File | null;
    spotify_artist: ValueType<GenericDropdownOption<number>, false>;
    related_artists: ValueType<RelatedArtistOptionType, true>;
};

export type ArtistFormProps = {
    handleSubmit: (values: ArtistFormValues, helpers: FormikHelpers<ArtistFormValues>) => void;
    initialValues?: ArtistFormValues;
    renderActions?: (props: FormikProps<ArtistFormValues>) => React.ReactNode;
};

const defaultInitialValues: ArtistFormProps['initialValues'] = {
    name: '',
    spotify_artist: null,
    related_artists: null,
    new_image: null,
};

const defaultRenderActions: ArtistFormProps['renderActions'] = ({ isSubmitting, submitForm }) => (
    <Button type="filled" color="black" onClick={submitForm} loading={isSubmitting}>
        Submit
    </Button>
);

const ArtistForm = ({
    handleSubmit,
    initialValues = defaultInitialValues,
    renderActions = defaultRenderActions,
}: ArtistFormProps) => {
    const [artistsPage, setArtistsPage] = useState(1);
    const [artistOptionsSearch, setArtistOptionsSearch] = useState('');

    const {
        data: artists,
        getCurrentPage,
        loading: artistsLoading,
        hasNextPage: hasNextPageForArtists,
    } = usePaginatedFetch(getArtists, {
        page: artistsPage,
        page_size: 10,
        search: artistOptionsSearch,
    });

    const fetchArtists = useMemo(() => debounce(getCurrentPage, 700), [getCurrentPage]);
    const artistOptions: ArtistFormValues['related_artists'] = useMemo(
        () =>
            artists.map((a) => ({
                value: a.id,
                label: a.name,
                image: a.image,
            })),
        [artists]
    );

    const [spotifyArtistsPage, setSpotifyArtistsPage] = useState(1);
    const [spotifyArtistsSearch, setSpotifyArtistsSearch] = useState('');
    const {
        data: spotifyArtists,
        hasNextPage: spotifyArtistsHasNextPage,
        loading: spotifyArtistsLoading,
        getCurrentPage: getSpotifyArtistsCurrentPage,
    } = usePaginatedFetch(getSpotifyArtists, {
        search: spotifyArtistsSearch,
        page: spotifyArtistsPage,
        page_size: 10,
    });

    const fetchSpotifyArtists = useMemo(
        () => debounce(getSpotifyArtistsCurrentPage, 700),
        [getSpotifyArtistsCurrentPage]
    );
    const spotifyArtistOptions: GenericDropdownOption<number>[] = useMemo(
        () =>
            spotifyArtists.map((a) => ({
                value: a.id,
                label: a.name,
            })),
        [spotifyArtists]
    );

    useAbortableEffect(
        (signal) => {
            fetchSpotifyArtists({ signal });
        },
        [fetchSpotifyArtists]
    );

    useAbortableEffect(
        (signal) => {
            fetchArtists({ signal });
        },
        [fetchArtists]
    );

    return (
        <Formik enableReinitialize initialValues={initialValues} onSubmit={handleSubmit}>
            {(props) => (
                <>
                    <Form>
                        <FormField>
                            <Label htmlFor="name">Name</Label>
                            <Field name="name" id="name" placeholder="Enter name" />
                        </FormField>
                        <FormField>
                            <Label htmlFor="spotify_artist">Spotify Artist</Label>
                            <Field name="spotify_artist" id="spotify_artist">
                                {(props: FieldProps) => (
                                    <Select
                                        isClearable
                                        styles={selectStyles}
                                        placeholder="Select spotify artist"
                                        inputValue={spotifyArtistsSearch}
                                        onInputChange={setSpotifyArtistsSearch}
                                        options={spotifyArtistOptions}
                                        value={props.field.value}
                                        onChange={(value) => props.form.setFieldValue('spotify_artist', value)}
                                        menuPortalTarget={document.body}
                                        onMenuScrollToBottom={() => {
                                            if (spotifyArtistsHasNextPage) {
                                                setSpotifyArtistsPage((page) => page + 1);
                                            }
                                        }}
                                        isLoading={spotifyArtistsLoading}
                                    />
                                )}
                            </Field>
                        </FormField>
                        <FormField>
                            <Label htmlFor="related_artists">Related Artists</Label>
                            <Field name="related_artists" id="related_artists">
                                {(props: FieldProps) => (
                                    <Select
                                        isMulti
                                        placeholder="Select related artists"
                                        inputValue={artistOptionsSearch}
                                        onInputChange={setArtistOptionsSearch}
                                        styles={selectStyles}
                                        value={props.field.value}
                                        onChange={(value) => props.form.setFieldValue('related_artists', value)}
                                        options={artistOptions}
                                        onMenuScrollToBottom={() => {
                                            if (hasNextPageForArtists) {
                                                setArtistsPage((page) => page + 1);
                                            }
                                        }}
                                        isLoading={artistsLoading}
                                        components={{
                                            Option: RelatedArtistOption,
                                        }}
                                        menuPortalTarget={document.body}
                                    />
                                )}
                            </Field>
                        </FormField>
                        <FormField>
                            <Label htmlFor="new_image">Image</Label>
                            <Field name="new_image" id="new_image">
                                {(props: FieldProps) => (
                                    <FileUpload
                                        files={[props.field.value]}
                                        onUpload={([firstFile]) => props.form.setFieldValue('new_image', firstFile)}
                                    >
                                        {({ files: [image] }) => (
                                            <FileUploadImagePreview className={styles.imagePreview} file={image} />
                                        )}
                                    </FileUpload>
                                )}
                            </Field>
                        </FormField>
                    </Form>
                    {renderActions(props)}
                </>
            )}
        </Formik>
    );
};

export default ArtistForm;
